// FLAGS: health-data, personal-data
// The app's `sessionStorage` accessor layer: the signed-in profile, the onboarding answers, the
// wellbeing and check-in drafts, the SSO flow marker, the expert filters and the session id. Most of the
// app's `sessionStorage` key names are declared in the block below; `utmParams` and `skip_translations`
// are the exceptions.
import { USER_DETAILS } from "@src/utils/constants";
import { QuestionList } from "@src/models/QuestionList";

const checkinSessionStorageIdentifier = "checkin";
const userSessionStorageIdentifier = "user";
const emailSessionStorageIdentifier = "onboarding_email";
const nameSessionStorageIdentifier = "onboarding_name";
const yearOfBirthSessionStorageIdentifier = "onboarding_year_of_birth";
const isManagerSessionStorageIdentifier = "onboarding_is_manager";
const referralSessionStorageIdentifier = "onboarding_referral";
const currentMoodsSessionStorageIdentifier = "onboarding_current_moods";
const hasDeskIdentifier = "onboarding_has_desk_job";
const generalWellbeingsessionStorageIdentifier = "general_wellbeing";
const resilienceCheckInStorageIdentifier = "resilience_checkin";
const preferencesSessionStorageIdentifier = "user_preferences";
const ssoProviderFlowStorageIdentifier = "sso_flow_provider";
const expertsFiltersThemesIdentifier = "experts_filters_themes";
const expertsFiltersLanguagesIdentifier = "experts_filters_languages";
const userSessionIdIdentifier = "userSessionId";
// FLAGS: silent-failure
// A parse failure is logged to the console and answered with the `USER_DETAILS` placeholder, so a corrupt
// entry reads as a valid empty user rather than as "not signed in".
const getUser = () => {
  if (typeof sessionStorage === "undefined") {
    return undefined;
  }

  try {
    const userDetails = sessionStorage.getItem(userSessionStorageIdentifier);
    if (userDetails) {
      return JSON.parse(userDetails);
    }

    return USER_DETAILS;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log(e);
    return USER_DETAILS;
  }
};

const saveEmailToStorage = (email) => {
  sessionStorage.setItem(emailSessionStorageIdentifier, email);
};
const getEmailFromStorage = () =>
  sessionStorage.getItem(emailSessionStorageIdentifier);
const clearEmailFromStorage = () => {
  sessionStorage.removeItem(emailSessionStorageIdentifier);
};

const saveNameToStorage = (name) => {
  sessionStorage.setItem(nameSessionStorageIdentifier, name);
};
const getNameFromStorage = () =>
  sessionStorage.getItem(nameSessionStorageIdentifier);
const clearNameFromStorage = () => {
  sessionStorage.removeItem(nameSessionStorageIdentifier);
};

const saveYearOfBirthToStorage = (yearOfBirth) => {
  sessionStorage.setItem(yearOfBirthSessionStorageIdentifier, yearOfBirth);
};
const getYearOfBirthFromStorage = () =>
  sessionStorage.getItem(yearOfBirthSessionStorageIdentifier);
const clearYearOfBirthFromStorage = () => {
  sessionStorage.removeItem(yearOfBirthSessionStorageIdentifier);
};

const saveIsManagerToStorage = (isManager) => {
  sessionStorage.setItem(isManagerSessionStorageIdentifier, isManager);
};
const getIsManagerFromStorage = () =>
  sessionStorage.getItem(isManagerSessionStorageIdentifier);
const clearIsManagerFromStorage = () => {
  sessionStorage.removeItem(isManagerSessionStorageIdentifier);
};

const getHasDeskJobFromStorage = (): boolean | null => {
  const hasDeskJob = sessionStorage.getItem(hasDeskIdentifier);
  if (hasDeskJob) {
    return hasDeskJob === "yes";
  }
  return null;
};
const saveHasDeskJobToStorage = (hasDeskJob: boolean) => {
  sessionStorage.setItem(hasDeskIdentifier, hasDeskJob ? "yes" : "no");
};
const clearHasDeskJobFromStorage = () => {
  sessionStorage.removeItem(hasDeskIdentifier);
};

// FLAGS: health-data
// The resilience check-in draft store: question keys and answer values merged in place under a key that
// nothing clears on submit. See views/resilience-checkin/MODULE.md for the key-name mismatch on the read
// side.
const saveResilienceCheckInAnswer = ({
  questionListKey = resilienceCheckInStorageIdentifier,
  languageCode,
  answers,
}: QuestionList) => {
  const currentlySavedQuestionList: QuestionList = JSON.parse(
    sessionStorage.getItem(questionListKey) || "{}",
  );
  const currentlySavedAnswers = currentlySavedQuestionList.answers || [];
  answers.forEach((answer) => {
    const index = currentlySavedAnswers.findIndex(
      (ans) => ans.questionKey === answer.questionKey,
    );
    if (index > -1) {
      currentlySavedAnswers[index] = answer;
    } else {
      currentlySavedAnswers.push(answer);
    }
  });
  sessionStorage.setItem(
    questionListKey,
    JSON.stringify({
      questionListKey,
      languageCode,
      answers: currentlySavedAnswers,
    }),
  );
};

const getResilienceCheckInAnswerByQuestionKey = (
  questionKey: string,
  questionListKey = resilienceCheckInStorageIdentifier,
) => {
  const currentlySavedQuestionList: QuestionList = JSON.parse(
    sessionStorage.getItem(questionListKey) || "{}",
  );
  const currentlySavedAnswers = currentlySavedQuestionList.answers || [];
  const index = currentlySavedAnswers.findIndex(
    (ans) => ans.questionKey === questionKey,
  );
  if (index > -1) {
    return currentlySavedAnswers[index];
  }
  return null;
};

const getResilineceCheckInAnswers = (
  questionListKey = resilienceCheckInStorageIdentifier,
): QuestionList => {
  return JSON.parse(sessionStorage.getItem(questionListKey)!);
};

// FLAGS: health-data
// The general-wellbeing answer, held for the life of the tab. `clearGeneralWellbeingFromStorage` is the
// only thing that removes it and `logout` does not call it.
const saveGeneralWellbeingToStorage = (happy) => {
  sessionStorage.setItem(
    generalWellbeingsessionStorageIdentifier,
    JSON.stringify(happy),
  );
};
const getGeneralWellbeingFromStorage = () => {
  if (!sessionStorage.getItem(generalWellbeingsessionStorageIdentifier)) {
    return {};
  }
  const generalWellbeing = JSON.parse(
    sessionStorage.getItem(generalWellbeingsessionStorageIdentifier) || "{}",
  );
  return generalWellbeing;
};
const clearGeneralWellbeingFromStorage = () => {
  sessionStorage.removeItem(generalWellbeingsessionStorageIdentifier);
};

const saveReferralToStorage = (referral) => {
  sessionStorage.setItem(
    referralSessionStorageIdentifier,
    JSON.stringify(referral),
  );
};
const getReferralFromStorage = () =>
  JSON.parse(sessionStorage.getItem(referralSessionStorageIdentifier)!);
const clearRefferalFromStorage = () => {
  sessionStorage.removeItem(referralSessionStorageIdentifier);
};

// FLAGS: health-data
// The onboarding mood selection. Removed by `clearOnboardingFromStorage`, which `logout` does not call,
// so it survives a sign-out inside the same tab.
const saveCurrentMoodsToStorage = (emotions) =>
  sessionStorage.setItem(
    currentMoodsSessionStorageIdentifier,
    JSON.stringify(emotions),
  );
const getCurrentMoodsFromStorage = () => {
  const emotionsString = sessionStorage.getItem(
    currentMoodsSessionStorageIdentifier,
  );
  if (emotionsString) {
    return new Set(JSON.parse(emotionsString));
  }
  return new Set();
};
const clearCurrentMoodsFromStorage = () =>
  sessionStorage.removeItem(currentMoodsSessionStorageIdentifier);

const dontLoadTranslations = () =>
  !!sessionStorage.getItem("skip_translations");

// FLAGS: personal-data
// A second writer of the `user` entry alongside auth.service's own, without its Application Insights
// stamp. Nothing under `src/` calls it today; wiring it up would let the two writers disagree about who
// the current user is.
const saveUserToStorage = (user) => {
  sessionStorage.setItem(userSessionStorageIdentifier, JSON.stringify(user));
};

const savePreferencesToStorage = (preferences) => {
  sessionStorage.setItem(
    preferencesSessionStorageIdentifier,
    JSON.stringify(preferences),
  );
};
const getPreferencesFromStorage = () => {
  const preferences = JSON.parse(
    sessionStorage.getItem(preferencesSessionStorageIdentifier)!,
  );
  if (preferences) {
    return Object.keys(preferences).filter((key) => preferences[key]);
  }
  return null;
};
const clearPreferencesFromStorage = () => {
  sessionStorage.removeItem(preferencesSessionStorageIdentifier);
};

const clearOnboardingFromStorage = () => {
  clearNameFromStorage();
  clearYearOfBirthFromStorage();
  clearRefferalFromStorage();
  clearCurrentMoodsFromStorage();
  clearIsManagerFromStorage();
  clearHasDeskJobFromStorage();
};

// FLAGS: personal-data
// Its result reaches Application Insights on every tracked exception, via `getEmailDomain`, so all
// telemetry from a signed-in user names their employer — see utils/MODULE.md, *Redaction coverage*.
const getUserEmail = (): string | undefined => {
  const user = getUser();
  return user?.email;
};

// FLAGS: health-data
// The Tripetto check-in fallback store: the whole answer set written here when the submit POST fails
// (tripetto.service.js), under a key `logout` does not remove.
const saveCheckinLocal = (formModules, formLanguage, answers) => {
  sessionStorage.setItem(
    checkinSessionStorageIdentifier,
    JSON.stringify({
      formModules,
      formLanguage,
      answers,
    }),
  );
};
const getCheckinLocal = () =>
  JSON.parse(sessionStorage.getItem(checkinSessionStorageIdentifier)!);
const hasCheckinLocal = () =>
  !!JSON.parse(sessionStorage.getItem(checkinSessionStorageIdentifier)!)
    ?.answers;
const removeCheckinLocal = () =>
  sessionStorage.removeItem(checkinSessionStorageIdentifier);

const saveSSOFlowProviderToStorage = (provider) => {
  sessionStorage.setItem(ssoProviderFlowStorageIdentifier, provider);
};

const getSSOFlowProviderFromStorage = () =>
  sessionStorage.getItem(ssoProviderFlowStorageIdentifier);
const clearSSOFlowFromStorage = () => {
  sessionStorage.removeItem(ssoProviderFlowStorageIdentifier);
};
const hasSSOFlowInStorage = () => {
  return getSSOFlowProviderFromStorage();
};

const saveExpertsFiltersToStorage = (filters: {
  themes?: string[];
  languages?: string[];
}) => {
  if (filters.themes !== undefined) {
    sessionStorage.setItem(
      expertsFiltersThemesIdentifier,
      JSON.stringify(filters.themes),
    );
  }
  if (filters.languages !== undefined) {
    sessionStorage.setItem(
      expertsFiltersLanguagesIdentifier,
      JSON.stringify(filters.languages),
    );
  }
};

const getExpertsFiltersFromStorage = (): {
  themes: string[];
  languages: string[];
} => {
  if (typeof sessionStorage === "undefined") {
    return { themes: [], languages: [] };
  }

  try {
    const themes = sessionStorage.getItem(expertsFiltersThemesIdentifier);
    const languages = sessionStorage.getItem(expertsFiltersLanguagesIdentifier);

    return {
      themes: themes ? JSON.parse(themes) : [],
      languages: languages ? JSON.parse(languages) : [],
    };
  } catch {
    return { themes: [], languages: [] };
  }
};

const clearExpertsFiltersFromStorage = () => {
  sessionStorage.removeItem(expertsFiltersThemesIdentifier);
  sessionStorage.removeItem(expertsFiltersLanguagesIdentifier);
};

const saveUserSessionIdToStorage = (id: string) => {
  sessionStorage.setItem(userSessionIdIdentifier, id);
};

const getUserSessionIdFromStorage = (): string | null =>
  sessionStorage.getItem(userSessionIdIdentifier);

const clearUserSessionIdFromStorage = () => {
  sessionStorage.removeItem(userSessionIdIdentifier);
};

const service = {
  saveEmailToStorage,
  getEmailFromStorage,
  clearEmailFromStorage,

  saveNameToStorage,
  getNameFromStorage,
  clearNameFromStorage,

  saveYearOfBirthToStorage,
  getYearOfBirthFromStorage,
  clearYearOfBirthFromStorage,

  saveIsManagerToStorage,
  getIsManagerFromStorage,
  clearIsManager: clearIsManagerFromStorage,

  saveHasDeskJobToStorage,
  getHasDeskJobFromStorage,
  clearHasDeskJobFromStorage,

  saveReferralToStorage,
  getReferralFromStorage,
  clearRefferalFromStorage,

  getUserEmail,
  getUser,
  saveUserToStorage,

  saveResilienceCheckInAnswer,
  getResilienceCheckInAnswerByQuestionKey,
  getResilineceCheckInAnswers,

  saveGeneralWellbeingToStorage,
  getGeneralWellbeingFromStorage,
  clearGeneralWellbeingFromStorage,

  saveCurrentMoodsToStorage,
  getCurrentMoodsFromStorage,
  clearCurrentMoodsFromStorage,

  savePreferencesToStorage,
  getPreferencesFromStorage,
  clearPreferencesFromStorage,

  clearOnboardingFromStorage,

  saveCheckinLocal,
  getCheckinLocal,
  hasCheckinLocal,
  removeCheckinLocal,

  dontLoadTranslations,

  saveSSOFlowProviderToStorage,
  getSSOFlowProviderFromStorage,
  hasSSOFlowInStorage,
  clearSSOFlowFromStorage,

  saveExpertsFiltersToStorage,
  getExpertsFiltersFromStorage,
  clearExpertsFiltersFromStorage,

  saveUserSessionIdToStorage,
  getUserSessionIdFromStorage,
  clearUserSessionIdFromStorage,
};

export default service;
